import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { formatCurrency } from '../../utils/currency';
import useCurrencyCode from '../../hooks/useCurrencyCode';
import useLiteProducts from './useLiteProducts';
import PhoneInput from '../../components/PhoneInput';

const PAYMENT_METHODS = ['Cash', 'Transfer', 'Card'];

export default function LiteSales() {
  const currencyCode = useCurrencyCode();
  const { products, categories, loading, reload } = useLiteProducts();
  const [search, setSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [showPreorderModal, setShowPreorderModal] = useState(false);
  const [preorderSubmitting, setPreorderSubmitting] = useState(false);
  const [preorderForm, setPreorderForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
  });

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return products.filter((p) => {
      const matchesSearch =
        !query ||
        p.name?.toLowerCase().includes(query) ||
        p.sku?.toLowerCase().includes(query) ||
        p.barcode?.toLowerCase().includes(query);
      const matchesCategory = !selectedCategoryId || p.categoryId === selectedCategoryId;
      return matchesSearch && matchesCategory;
    });
  }, [products, search, selectedCategoryId]);

  const cartTotal = useMemo(
    () =>
      cart.reduce(
        (sum, item) => sum + Number(item.unitPrice || 0) * Number(item.quantity || 0),
        0
      ),
    [cart]
  );

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    [cart]
  );

  const addToCart = (product) => {
    const unitPrice = Number(product.priceInclTax ?? product.price ?? 0);
    setCart((items) => {
      const existing = items.find((i) => i.id === product.id);
      if (existing) {
        return items.map((i) =>
          i.id === product.id ? { ...i, quantity: Number(i.quantity || 0) + 1 } : i
        );
      }
      return [
        ...items,
        {
          id: product.id,
          name: product.name,
          unitPrice,
          quantity: 1,
        },
      ];
    });
  };

  const updateQuantity = (id, nextQty) => {
    const qty = Math.max(1, Number(nextQty) || 1);
    setCart((items) => items.map((item) => (item.id === id ? { ...item, quantity: qty } : item)));
  };

  const removeFromCart = (id) => setCart((items) => items.filter((item) => item.id !== id));

  const startCheckout = () => {
    if (cart.length === 0) {
      toast.error('Add at least one product.');
      return;
    }
    setShowCheckout(true);
  };

  const confirmCheckout = async () => {
    if (cart.length === 0) return;
    setSubmitting(true);
    const payload = {
      total: cartTotal,
      totalDiscount: 0,
      paymentMethod,
      items: cart.map((item) => ({
        productName: item.name,
        quantity: Number(item.quantity || 0),
        unitPrice: Number(item.unitPrice || 0),
        discount: 0,
        subtotal: Number(item.unitPrice || 0) * Number(item.quantity || 0),
      })),
    };

    try {
      await api.post('/receipts', payload);
      toast.success('Sale completed.');
      setCart([]);
      setShowCheckout(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not complete sale.');
    } finally {
      setSubmitting(false);
    }
  };

  const openPreorderModal = () => {
    if (cart.length === 0) {
      toast.error('Add items before creating a pre-order.');
      return;
    }
    setShowPreorderModal(true);
  };

  const submitPreorder = async () => {
    if (cart.length === 0) {
      toast.error('Add items before creating a pre-order.');
      return;
    }
    if (!preorderForm.name.trim() || !preorderForm.phone.trim()) {
      toast.error('Customer name and phone are required.');
      return;
    }
    setPreorderSubmitting(true);
    try {
      const payload = {
        customerName: preorderForm.name.trim(),
        customerWhatsApp: preorderForm.phone.trim(),
        customerEmail: preorderForm.email.trim() || null,
        customerAddress: preorderForm.address.trim() || null,
        notes: preorderForm.notes.trim() || null,
        source: 'staff',
        items: cart.map((item) => ({
          productId: item.id,
          productName: item.name,
          quantity: Number(item.quantity || 0),
          unitPrice: Number(item.unitPrice || 0),
        })),
      };
      await api.post('/staff/preorders', payload);
      toast.success('Pre-order saved.');
      setPreorderSubmitting(false);
      setShowPreorderModal(false);
      setCart([]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create pre-order.');
      setPreorderSubmitting(false);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setSelectedCategoryId(null);
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-emerald-300">Sales</p>
          <h1 className="text-2xl font-semibold text-white">Sales Lite</h1>
          <p className="text-sm text-slate-400">Search, add to cart, and checkout fast.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={reload}
            className="rounded-full border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:border-emerald-400"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="rounded-full border border-slate-800 px-3 py-2 text-xs font-semibold text-slate-300 transition hover:border-slate-600"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={openPreorderModal}
            disabled={cart.length === 0}
            className="rounded-full border border-emerald-500 bg-emerald-600/10 px-3 py-2 text-xs font-semibold text-emerald-200 transition hover:bg-emerald-600/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Pre-Order
          </button>
        </div>
      </header>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-3 shadow-inner shadow-black/30">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products by name, SKU, or barcode"
          className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        />
        <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-2">
          <CategoryPill
            label="All"
            active={selectedCategoryId === null}
            onClick={() => setSelectedCategoryId(null)}
          />
          {categories.map((cat) => (
            <CategoryPill
              key={cat.id}
              label={cat.name}
              active={selectedCategoryId === cat.id}
              onClick={() => setSelectedCategoryId(cat.id)}
            />
          ))}
        </div>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{filteredProducts.length} product{filteredProducts.length === 1 ? '' : 's'}</span>
          <span className="rounded-full border border-slate-800 bg-slate-900 px-3 py-1">
            Tap to add
          </span>
        </div>

        {loading && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-center text-sm text-slate-400">
            Loading products...
          </div>
        )}

        {!loading && filteredProducts.length === 0 && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-center text-sm text-slate-400">
            No products match this search.
          </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          {filteredProducts.map((product) => (
            <article
              key={product.id}
              className="flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-sm shadow-black/20"
            >
              <div className="flex flex-col gap-1">
                <p className="text-base font-semibold text-white">{product.name}</p>
                <p className="text-sm font-semibold text-emerald-300">
                  {formatCurrency(product.priceInclTax ?? product.price ?? 0, currencyCode)}
                </p>
                <p className="text-xs text-slate-400">Stock {product.stock ?? 'N/A'}</p>
              </div>
              <button
                type="button"
                onClick={() => addToCart(product)}
                className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400"
              >
                Add
              </button>
            </article>
          ))}
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-24 z-30 mx-auto max-w-xl px-4">
        <div className="flex items-center justify-between rounded-2xl border border-emerald-500/40 bg-slate-950/95 px-4 py-3 text-white shadow-[0_15px_35px_-20px_rgba(16,185,129,0.6)] backdrop-blur">
          <div className="flex flex-col">
            <span className="text-sm font-semibold">{cartCount} item{cartCount === 1 ? '' : 's'}</span>
            <span className="text-base font-bold text-emerald-300">
              {formatCurrency(cartTotal, currencyCode)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCart([])}
              disabled={cart.length === 0}
              className="rounded-lg border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={startCheckout}
              disabled={cart.length === 0}
              className="rounded-xl bg-emerald-500 px-4 py-3 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Checkout
            </button>
          </div>
        </div>
      </div>

      {showCheckout && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/80 px-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Checkout</p>
                <h3 className="text-xl font-semibold text-white">Confirm payment</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCheckout(false)}
                className="rounded-full border border-slate-700 px-3 py-1 text-xs uppercase tracking-wide text-slate-300 hover:border-slate-500"
              >
                Close
              </button>
            </div>

            <div className="mt-4 space-y-4">
              <div className="rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Items</span>
                  <span className="font-semibold text-white">{cartCount}</span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-slate-300">Total</span>
                  <span className="text-lg font-bold text-emerald-300">
                    {formatCurrency(cartTotal, currencyCode)}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Payment method</p>
                <div className="grid grid-cols-3 gap-2">
                  {PAYMENT_METHODS.map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setPaymentMethod(method)}
                      className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                        paymentMethod === method
                          ? 'border-emerald-500 bg-emerald-600/15 text-emerald-200'
                          : 'border-slate-800 bg-slate-950 text-slate-200 hover:border-slate-700'
                      }`}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Cart</p>
                <div className="divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-950/60">
                  {cart.map((item) => (
                    <div key={item.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-white">{item.name}</span>
                        <span className="text-xs text-slate-400">
                          {formatCurrency(item.unitPrice, currencyCode)} each
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.id, e.target.value)}
                          className="w-16 rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-center text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                        />
                        <button
                          type="button"
                          onClick={() => removeFromCart(item.id)}
                          className="rounded-full border border-rose-500/60 px-2 py-1 text-xs font-semibold text-rose-200 hover:bg-rose-500/10"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  {cart.length === 0 && (
                    <p className="px-4 py-3 text-sm text-slate-400">Cart is empty.</p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCheckout(false);
                    openPreorderModal();
                  }}
                  disabled={submitting || cart.length === 0}
                  className="rounded-lg border border-emerald-500 px-4 py-2 text-sm font-semibold text-emerald-200 transition hover:bg-emerald-600/10 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Save Pre-Order
                </button>
                <button
                  type="button"
                  onClick={() => setShowCheckout(false)}
                  className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmCheckout}
                  disabled={submitting || cart.length === 0}
                  className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPreorderModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/80 px-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Pre-Order</p>
                <h3 className="text-xl font-semibold text-white">Save cart as pre-order</h3>
                <p className="text-sm text-slate-400">Collect contact details and hold this cart.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPreorderModal(false)}
                className="rounded-full border border-slate-700 px-3 py-1 text-xs uppercase tracking-wide text-slate-300 hover:border-slate-500"
              >
                Close
              </button>
            </div>

            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-1 gap-3">
                <label className="text-sm text-slate-200">
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Customer name</span>
                  <input
                    type="text"
                    value={preorderForm.name}
                    onChange={(e) => setPreorderForm((f) => ({ ...f, name: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  />
                </label>
                <PhoneInput
                  label="Phone / WhatsApp"
                  value={preorderForm.phone}
                  onChange={(value) => setPreorderForm((f) => ({ ...f, phone: value }))}
                  wrapperClassName="text-sm text-slate-200"
                  placeholder="e.g. 7900 999 000"
                />
                <label className="text-sm text-slate-200">
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Email (optional)</span>
                  <input
                    type="email"
                    value={preorderForm.email}
                    onChange={(e) => setPreorderForm((f) => ({ ...f, email: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  />
                </label>
                <label className="text-sm text-slate-200">
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Address (optional)</span>
                  <input
                    type="text"
                    value={preorderForm.address}
                    onChange={(e) => setPreorderForm((f) => ({ ...f, address: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  />
                </label>
                <label className="text-sm text-slate-200">
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Notes (optional)</span>
                  <textarea
                    rows={3}
                    value={preorderForm.notes}
                    onChange={(e) => setPreorderForm((f) => ({ ...f, notes: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  />
                </label>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-slate-400">
                {cartCount} item{cartCount === 1 ? '' : 's'} | {formatCurrency(cartTotal, currencyCode)}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowPreorderModal(false)}
                  className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitPreorder}
                  disabled={preorderSubmitting}
                  className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {preorderSubmitting ? 'Saving...' : 'Save pre-order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryPill({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition ${
        active
          ? 'bg-emerald-500 text-slate-900'
          : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
      }`}
    >
      {label}
    </button>
  );
}
